import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseResourceItemsComponent, DateFormatPipe, EddyAutoComplete, FuncaoService, GlobalService, Login, Page, Produto, ProdutoUnidade, RequisicaoItemMovimento, RequisicaoMovimento, SaldoEstoqueService, SetorAlmoxarifado } from 'eddydata-lib';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ProdutoUnidadeService } from '../../produto/service/produto-unidade.service';
import { ProdutoService } from '../../produto/service/produto.service';
import { info, warning } from 'toastr';

@Component({
  selector: 'lib-requisicao-autorizacao-item',
  templateUrl: './requisicao-autorizacao-item.component.html'
})
export class RequisicaoAutorizacaoItemComponent extends BaseResourceItemsComponent<RequisicaoItemMovimento> implements OnChanges {

  // ========================================================================
  // ----------------------- DECLARAÇÃO DE VARIAVEIS ------------------------
  // ========================================================================

  @Input() somenteVisualizar: boolean = false;
  @Input() requisicao: RequisicaoMovimento;
  @Input() setor_almoxarifado: SetorAlmoxarifado;
  @Input() login: Login;

  public ptBR: any;

  public produtoAutoComplete: EddyAutoComplete<Produto>;

  public listProdUn: ProdutoUnidade[];
  public listLoteVec: any[];
  public listLoteVecFiltro: any[];

  public itemTroca: RequisicaoItemMovimento;
  public indexTroca: number;
  public visualizarTroca: boolean = false;

  public visualizarItemDuplicado: boolean = false;

  public itemDuplicar: RequisicaoItemMovimento;
  private indexDuplicar: number;
  public qtdeDuplicar: number = 0;

  public imaskValor = {
    mask: Number,
    scale: 5,
    signed: false,
    thousandsSeparator: '.',
    padFractionalZeros: true,
    normalizeZeros: true,
    radix: ','
  };

  // ========================================================================
  // -------------------------- MÉTODOS ABSTRAÍDOS --------------------------
  // ========================================================================

  constructor(private produtoUnService: ProdutoUnidadeService,
    private saldoEstoqueService: SaldoEstoqueService,
    public funcaoService: FuncaoService,
    public globalService: GlobalService,
    private produtoService: ProdutoService) {
    super(new RequisicaoItemMovimento(), produtoService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.requisicao) {
      this.carregarAutoCompletes();
    }
    if (this.lista?.length > 0) this.lista.forEach((i) => this.carregarMedia(i));
  }

  protected afterInit(): void {
    this.ptBR = new GlobalService().obterDataBR();
    this.carregarAutoCompletes();
  }

  protected validSave(item: RequisicaoItemMovimento): boolean {
    if (!item.produto_unidade || !item.produto_unidade.id) {
      warning('Informe o produto e a unidade!');
      return false;
    }

    if (item.produto_unidade_troca && !item.justificativa_troca) {
      warning('Informe a justificativa de troca!');
      return false;
    }

    if (!this.validarSaldo(item))
      return false;

    if (this.login.parametro['requisicao']?.exibir_saldo_almoxarifado && +item.qtd_requisitada > +item['saldo_atual_estoque']) {
      warning('Quantidade solicitada do item maior é maior que a disponível no estoque.');
      return false;
    }

    const incluido = this.lista.find((i) =>
      !i['editavel']
      && item.produto_unidade.id === i.produto_unidade.id
      && item.lote === i.lote
      && this.mesmoVencimento(item.vencimento, i.vencimento)
    );
    if (incluido) {
      warning(`O produto ${item.produto_unidade.produto.nome} com o lote ${item.lote} e vencimento informado já foi incluído.`);
      return false;
    }

    return true
  }

  protected beforeSave(item: RequisicaoItemMovimento): void {
    this.listProdUn = [];
  }

  protected afterEdit(item: RequisicaoItemMovimento) {
    this.carregarUnidades(item);
    item.vencimento = new DateFormatPipe().transform(item.vencimento, []);
  }

  public disabled() {
    if (!this.setor_almoxarifado)
      return true;
    return super.disabled();
  }

  // ========================================================================
  // -------------------------- MÉTODOS DA CLASSE ---------------------------
  // ========================================================================

  private carregarAutoCompletes() {
    if (!this.requisicao)
      return;
    this.produtoAutoComplete = ProdutoService.autoCompleteCodigoCompleto(null, this.produtoService,
      'id', ['codigo', 'nome'], { orgao_id: this.login.orgao.id, orderBy: 'codigo,nome' }, { number: ['id', 'codigo'], text: ['codigo', 'nome'] }, null, null, null,
      (pagina: number, limite: number, filtros: {}, filtroStr: string): Observable<Page> => {
        let parametros: { codigo?: string, nome?: string } = {};
        if (new FuncaoService().isNumerico(filtroStr))
          parametros.codigo = filtroStr;
        else
          parametros.nome = filtroStr;
        return this.saldoEstoqueService
          .obterProdutosComSaldo(this.setor_almoxarifado.id, parametros)
          .pipe(map(res => {
            let page = new Page();
            page.content = res;
            return page;
          }));
      }
    );
  }

  public carregarUnidades(item: RequisicaoItemMovimento) {
    if (!item.produto_unidade.produto) {
      return;
    }
    this.listLoteVec = [];
    this.listLoteVecFiltro = [];
    this.produtoUnService.filtrarCodigoCompleto(0, 0, {
      produto_id: item.produto_unidade.produto.id,
      ativo: true,
      relations: 'produto,unidade',
      orderBy: 'unidade.nome'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listProdUn = res ? res.content : [];
        if (this.listProdUn.length === 0) {
          warning('Nenhuma unidade ativa foi localizada para este produto!');
          return;
        }
        if (!item.produto_unidade || !item.produto_unidade.id) {
          item.produto_unidade = this.listProdUn[0];
        }
        this.carregarMedia(item);
        this.carregarLotes(item);
      }, error => error(error.message ? error.message : error));
  }

  public carregarLotes(item: RequisicaoItemMovimento) {
    if (!item.produto_unidade || !item.produto_unidade.id)
      return;
    this.listLoteVec = [];
    this.listLoteVecFiltro = [];
    this.saldoEstoqueService
      .saldoProdutoUnidadeLoteVencimento(item.produto_unidade.id, this.setor_almoxarifado.id, { nao_obg_saldo: true })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (saldos) => {
        if (!saldos || saldos.length === 0)
          return;
        this.listLoteVec = saldos;
        if (!item.lote || !item.vencimento) {
          let saldo = saldos[0];
          this.onChangeLote(item, saldo);
        } else {
          let itemL = saldos.find((s) => s.lote === item.lote
            && new Date(new Date(s.vencimento).toDateString()).getTime() === new Date(new Date(item.vencimento).toDateString()).getTime());
          this.onChangeLote(item, itemL);
        }
      });
  }

  onChangeLote(item: RequisicaoItemMovimento, itemL) {
    if (item.vencimento < itemL.vencimento) {
      warning('Atenção! Existem lotes com vencimentos anteriores, por favor verifique!');
    }

    item.lote = itemL.lote;
    item.vencimento = itemL.vencimento;
    item['saldo_atual'] = itemL.saldo_atual;
    item['saida'] = itemL.saida;
  }

  public trocar(item: RequisicaoItemMovimento) {
    this.itemTroca = item;
    this.visualizarTroca = true;
  }

  public trocatItem(item: RequisicaoItemMovimento) {
    item.produto_unidade['itens'] = []
    this.valorTotal(item);
    let itemList = this.lista.find((i) => i.id === item.id);
    this.lista[this.lista.indexOf(itemList)] = item;
  }

  public reverterTroca(item: RequisicaoItemMovimento) {
    if (item.produto_unidade_troca) {
      item.produto_unidade = item.produto_unidade_troca;
      this.saldoEstoqueService
        .saldoProdutoUnidadeLoteVencimento(item.produto_unidade.id, this.login.setorAlmoxarifado.id, { nao_obg_saldo: true })
        .subscribe((saldos) => {
          if (!saldos || saldos.length === 0) {
            item.lote = null;
            item.vencimento = null;
            item['saldo_atual'] = null;
            item['saida'] = null;
            return;
          }
          let saldo = saldos.sort((a, b) => new Date(new Date(a.vencimento).toDateString()).getTime() - new Date(new Date(b.vencimento).toDateString()).getTime())[0];
          item.lote = saldo.lote;
          item.vencimento = new DateFormatPipe().transform(saldo.vencimento, []);
          item['saldo_atual'] = +saldo.saldo_atual;
          item['saida'] = +saldo.saida;
        });

      item.produto_unidade_troca = null;
      item.justificativa_troca = null;
      item.qtd_aceita = 0;
    }
  }

  public valorTotal(item: RequisicaoItemMovimento, sem_notificacao?: boolean) {
    const saldo = this.requisicao.devolucao ? item['saida'] : item['saldo_atual'];
    if (!saldo) {
      if (!sem_notificacao)
        warning(`Item não possui saldo para requição.`);
      return;
    } else if (saldo >= +item.qtd_aceita) {
      if (saldo >= +item.qtd_requisitada) {
        if (!item.qtd_aceita || +item.qtd_aceita <= 0) {
          item.qtd_aceita = item.qtd_requisitada;
        }
      } else if (saldo < +item.qtd_requisitada) {
        if (!item.qtd_aceita || +item.qtd_aceita <= 0) {
          item.qtd_aceita = saldo;
        }
      }
    } else {
      if (!sem_notificacao)
        warning(`Saldo insuficiente para a requisição, valor maximo adicionado.`);
      item.qtd_aceita = saldo;
    }
  }

  buscarLoteVencimento(event) {
    if (event.query)
      this.listLoteVecFiltro = this.listLoteVec.filter((l) => l.lote.toLowerCase().includes(event.query.toLowerCase()));
    else
      this.listLoteVecFiltro = Object.assign([], this.listLoteVec)
  }

  conversorLote(itemL) {
    if (!itemL || !itemL.lote || !itemL.vencimento)
      return '';
    return `${itemL.lote} - ${new FuncaoService().converteDataBR(itemL.vencimento)}`
  }

  public compareFnLoteVencimento(c1: any, c2: any): boolean {
    return c1 && c2 && c1.lote && c2.lote && c1.vencimento && c2.vencimento
      && c1.lote === c2.lote && new Date(c1.vencimento).toDateString() === new Date(c2.vencimento).toDateString();
  }

  private mesmoVencimento(data1: any, data2: any): boolean {
    return !!data1 && !!data2
      && new Date(data1).toDateString() === new Date(data2).toDateString();
  }

  private validarSaldo(item: RequisicaoItemMovimento, sem_notificacao?: boolean) {
    if (+item.qtd_aceita > (+item.saldo_atual)) {
      if (!sem_notificacao)
        warning('Quantidade aceita maior que o saldo atual!');
      return false;
    }

    return true;
  }

  public onBlurQtd(item: RequisicaoItemMovimento) {
    if (!this.validarSaldo(item))
      setTimeout(() => {
        item.qtd_aceita = item['saldo_atual'];
      }, 100);
  }

  public preencherItemComSaldo(item: RequisicaoItemMovimento) {
    if (!item.qtd_aceita || +item.qtd_aceita <= 0) {
      this.valorTotal(item);
    } else {
      item.qtd_aceita = 0;
    }
  }

  public todosSelecionados(): boolean {
    return !this.lista || this.lista
      .filter((r) => !r['checked']).length === 0
  }

  public cancelar(item: RequisicaoItemMovimento, index: number) {
    if (item?.['duplicacao_index_origem'] !== undefined && item?.['duplicacao_qtd'] !== undefined) {
      const itemOriginal = this.lista[item['duplicacao_index_origem']];
      if (itemOriginal) {
        itemOriginal.qtd_requisitada = +itemOriginal.qtd_requisitada + +item['duplicacao_qtd'];
      }

      this.lista.splice(index, 1);
      this.anterior = null;
      this.change();
      info('Item removido porque a duplicação foi cancelada.');
      return;
    }

    super.cancelar(item, index);
  }

  public selecionarTodos() {
    if (!this.lista)
      return;
    if (this.todosSelecionados())
      this.lista.forEach((r) => {
        r.qtd_aceita = 0
        r['checked'] = false;
      });
    else
      this.lista.forEach((r) => {
        if (!r.qtd_aceita || +r.qtd_aceita <= 0)
          this.valorTotal(r, true);
        r['checked'] = true;
      });
  }

  public duplicar(item: RequisicaoItemMovimento, index: number) {
    this.itemDuplicar = item;
    this.indexDuplicar = index;
    this.qtdeDuplicar = item.qtd_requisitada;
    setTimeout(() => {
      this.visualizarItemDuplicado = true;
    }, 100);
  }

  public adicionarValorDuplicado() {
    if (!this.itemDuplicar)
      return;

    if (+this.qtdeDuplicar >= +this.itemDuplicar.qtd_requisitada) {
      warning(`Quantidade informada tem que ser menor que a quantidade do item duplicado(${this.funcaoService.convertToBrNumber(this.itemDuplicar.qtd_requisitada, 2)})`);
      return;
    }

    let duplicado = Object.assign({}, this.itemDuplicar);
    duplicado.id = null;
    duplicado.lote = null;
    duplicado.vencimento = null;
    duplicado['duplicacao_index_origem'] = this.indexDuplicar;
    duplicado['duplicacao_qtd'] = this.qtdeDuplicar;

    duplicado.qtd_requisitada = this.qtdeDuplicar;
    this.itemDuplicar.qtd_requisitada = +this.itemDuplicar.qtd_requisitada - +this.qtdeDuplicar;

    this.carregarMedia(duplicado);
    this.lista.splice(this.indexDuplicar + 1, 0, duplicado);

    setTimeout(() => {
      this.editar(duplicado)
    }, 100);

    this.visualizarItemDuplicado = false;
    this.itemDuplicar = null;
    this.indexDuplicar = null;
  }

  public carregarMedia(item: RequisicaoItemMovimento) {
    if (item.produto_unidade.id) {
      this.saldoEstoqueService.mediaProdutoAlmoxarifado(this.login.orgao.id, item.produto_unidade.id, this.requisicao.setor_almoxarifado?.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          item['media_consumo_mensal'] = res?.media_consumo_mensal ? res?.media_consumo_mensal : 0;
        });
    }
  }

}
